import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NORMAL_PRICE, queryKeys, TEXT_STRING } from '../../../../constants';
import { TCreateProject, TRecommendFemale } from 'types';
import { useChatStore } from 'store';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createProjectOrder, getProjectInformation } from 'api';
import { combineDateTime, getFormattedAmount } from 'utils';
import { FIELD_NAME, initialValueCallWoman } from 'helpers';
import { useToast } from 'hooks';
import { CallWoman } from 'components';
import { CallWomanRef } from 'components/OrderScreen/CallWoman';
import { toast } from 'react-toastify';

type Props = {
  projectId: number;
  areaId: number;
};

function AddGirl({ projectId, areaId }: Props) {
  // State
  const [formStateAddGirl, setFormStateAddGirl] = useState<
    Record<string, string>
  >(initialValueCallWoman);
  const [listSelectedGirl, setListSelectedGirl] = useState<TRecommendFemale[]>(
    [],
  );

  const callWomanRef = useRef<CallWomanRef>(null);

  // Hook
  const { setActionChatStep } = useChatStore();
  const { toastError } = useToast();

  const { data: projectInfo } = useQuery({
    queryKey: [queryKeys.PROJECT_INFO],
    queryFn: getProjectInformation,
    refetchOnMount: false,
  });

  const { mutateAsync: createOrderMutate, isPending } = useMutation({
    mutationFn: (body: TCreateProject) => {
      return createProjectOrder(body);
    },
    onSuccess: () => {
      toast.success(TEXT_STRING.SUMMARY_ORDER.ORDER_THANK);
    },
    onError(error) {
      toastError(error);
    },
  });

  const totalPriceGirl = useMemo(() => {
    const setCount = +formStateAddGirl[FIELD_NAME.SET];
    const peopleCount = +formStateAddGirl[FIELD_NAME.PEOPLE];
    const girlCount = peopleCount - listSelectedGirl.length;

    const total =
      listSelectedGirl.reduce((result, current) => {
        return result + NORMAL_PRICE * setCount + current.nominationFee;
      }, 0) +
      girlCount * NORMAL_PRICE * setCount;

    return getFormattedAmount(total);
  }, [formStateAddGirl, listSelectedGirl]);

  const clearState = useCallback(() => {
    setActionChatStep();
    setFormStateAddGirl(initialValueCallWoman);
    setListSelectedGirl([]);
  }, [setActionChatStep]);

  const handleOrderGirl = useCallback(async () => {
    const isValid = callWomanRef.current?.handleValidate();
    if (!isValid) return;

    const dateTime = combineDateTime(
      formStateAddGirl[FIELD_NAME.DATE],
      formStateAddGirl[FIELD_NAME.TIME],
    ).toISOString();
    const data = {
      setCount: +formStateAddGirl[FIELD_NAME.SET],
      peopleCount: +formStateAddGirl[FIELD_NAME.PEOPLE],
      startDate: dateTime,
      projectId: projectId,
      ProjectCandidate: listSelectedGirl.map((girl) => girl.id),
      estimatePrice: totalPriceGirl,
    };
    const areaId = +formStateAddGirl[FIELD_NAME.AREA_ID];
    try {
      await createOrderMutate(areaId ? { ...data, areaId } : data);
      clearState();
    } catch (error) {
      console.log(error);
    }
  }, [
    clearState,
    createOrderMutate,
    formStateAddGirl,
    listSelectedGirl,
    projectId,
    totalPriceGirl,
  ]);

  useEffect(() => {
    setFormStateAddGirl((prev) =>
      !!areaId ? { ...prev, areaId: `${areaId}` } : prev,
    );
  }, [areaId]);

  return (
    <CallWoman
      ref={callWomanRef}
      projectInfo={projectInfo}
      formState={formStateAddGirl}
      setFormState={setFormStateAddGirl}
      listSelectedGirl={listSelectedGirl}
      setListSelectedGirl={setListSelectedGirl}
      estimatePrice={totalPriceGirl}
      onOrder={handleOrderGirl}
      loading={isPending}
    />
  );
}

export default AddGirl;
